import React from 'react';
import { Card, Text, Button, StatusChip } from '@fragments';
import {
    IconBrandApple,
    IconBrandSpotify,
    IconBrandYoutube,
    IconBrandAmazon,
    IconMusic,
    IconReceipt,
} from '@tabler/icons-react';
import './styles.scss';

/**
 * MySubscriptions — a faithful replica of the AlignUI finance dashboard
 * "My Subscriptions" card.
 *
 * Flat <Card> (crisp white surface on a gray canvas) with a "See All" text
 * Button in the header. The body opens with a soft primary-washed promo banner,
 * then a divider-ruled list of exactly three subscription rows: a brand chip,
 * the service name over a sub-label, and a trailing price-over-status column.
 */

// ── Mock data (inline, self-contained) ───────────────────────────────────────
// `tint` keys the brand wash on the leading chip; `status` is the trailing pill.
const SUBSCRIPTIONS = [
    {
        id: 'spotify',
        name: 'Spotify',
        sub: '$7.99 /month',
        price: '$7.99',
        tint: 'spotify',
        Icon: IconBrandSpotify,
        status: <StatusChip.Success text="Paid" />,
    },
    {
        id: 'youtube',
        name: 'YouTube Music',
        sub: '$79.99 /year',
        price: '$79.99',
        tint: 'youtube',
        Icon: IconBrandYoutube,
        status: <StatusChip.Alert text="Expiring" />,
    },
    {
        id: 'amazon',
        name: 'Prime Video',
        sub: '$9.99 /month',
        price: '$9.99',
        tint: 'amazon',
        Icon: IconBrandAmazon,
        status: <StatusChip.Default text="Paused" />,
    },
];

export default function MySubscriptions() {
    return (
        <Card
            flat
            noBodyPadding
            title="My Subscriptions"
            icon={<IconReceipt />}
            action={
                <Button variant="text" color="inverse" size="small">
                    See All
                </Button>
            }
        >
            <div className="my-subs">
                {/* Promo banner — soft primary wash, brand glyph, copy + CTA. */}
                <div className="my-subs__promo">
                    <span className="my-subs__promo-media" aria-hidden="true">
                        <span className="my-subs__promo-tile my-subs__promo-tile--back">
                            <IconMusic />
                        </span>
                        <span className="my-subs__promo-tile my-subs__promo-tile--front">
                            <IconBrandApple />
                        </span>
                    </span>

                    <div className="my-subs__promo-copy">
                        <Text.label contrast={9}>50% discount on Apple Music</Text.label>
                        <Text.caption contrast={6}>For only $4.99 per month!</Text.caption>
                    </div>

                    <span className="my-subs__promo-cta">
                        <Button variant="text" color="primary" size="small">
                            Learn More
                        </Button>
                    </span>
                </div>

                {/* Divider-ruled subscription list — exactly three rows. */}
                <ul className="my-subs__list">
                    {SUBSCRIPTIONS.map(({ id, name, sub, price, tint, Icon, status }) => (
                        <li key={id} className="my-subs__row">
                            <span
                                className={`my-subs__chip my-subs__chip--${tint}`}
                                aria-hidden="true"
                            >
                                <Icon />
                            </span>

                            <div className="my-subs__info">
                                <Text.label contrast={9} maxLines={1}>
                                    {name}
                                </Text.label>
                                <Text.caption contrast={6} maxLines={1}>
                                    {sub}
                                </Text.caption>
                            </div>

                            <div className="my-subs__trailing">
                                <Text.label contrast={9} maxLines={1}>{price}</Text.label>
                                {status}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </Card>
    );
}
